// T-333 — Recupero registrazioni postume (temperature + pulizie).
//
// Quando un controllo HACCP viene registrato in ritardo (l'operatore lo ha
// eseguito un giorno passato ma lo compila oggi), l'evento append-only viene
// scritto col `client_ts` retrodatato al giorno reale + l'ORA REALE dichiarata
// dall'operatore (NON casuale: un orario inventato perde credibilità verso
// l'ASL). Il `server_ts` resta `now()` reale → l'audit dice onestamente
// "operazione del giorno X, registrata il giorno Y".
//
// Invariante (verificata): la hash chain è `prev_hash|kind|payload` — NON include
// i timestamp — quindi retrodatare `client_ts` non rompe la catena né
// l'append-only. I campi marker sono additivi/opzionali nei payload schema
// (vedi packages/shared/src/domain/event.ts).
//
// Questo modulo contiene SOLO logica pura e testabile (date locali + calcolo
// "voci mancanti per giorno"); la UI vive nelle pagine HACCP.

/** Finestra di recupero di default: quanti giorni passati mostrare. */
export const LATE_ENTRY_LOOKBACK_DAYS = 30;

/** Fascia oraria suggerita di default per l'ora dichiarata (ora locale). */
export const LATE_ENTRY_DEFAULT_TIME = '12:00';

const pad2 = (n: number): string => String(n).padStart(2, '0');

/** Chiave 'YYYY-MM-DD' del giorno di calendario LOCALE di una Date. */
export function localDateKey(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

/** Chiave 'YYYY-MM-DD' locale a partire da un timestamp ISO (o ms). */
export function localDateKeyOf(tsIsoOrMs: string | number): string | null {
  const d = new Date(tsIsoOrMs);
  if (Number.isNaN(d.getTime())) return null;
  return localDateKey(d);
}

/**
 * Combina un giorno locale 'YYYY-MM-DD' + un'ora 'HH:mm' in un timestamp ISO,
 * interpretando data e ora nel fuso LOCALE del device (come collettivaLotCode /
 * computeLabelExpiration). Restituisce la stringa ISO (UTC) per `client_ts`.
 * Lancia su input malformati: la UI passa sempre valori validati dai picker.
 */
export function combineLocalDateTime(dayKey: string, hhmm: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dayKey);
  const t = /^(\d{1,2}):(\d{2})$/.exec(hhmm);
  if (!m) throw new Error(`Data non valida: ${dayKey}`);
  if (!t) throw new Error(`Ora non valida: ${hhmm}`);
  const [, y, mo, d] = m;
  const [, hh, mm] = t;
  const hN = Number(hh);
  const mN = Number(mm);
  if (hN > 23 || mN > 59) throw new Error(`Ora fuori range: ${hhmm}`);
  const date = new Date(Number(y), Number(mo) - 1, Number(d), hN, mN, 0, 0);
  if (Number.isNaN(date.getTime())) throw new Error(`Data/ora non valida: ${dayKey} ${hhmm}`);
  return date.toISOString();
}

/** Marker di audit per una registrazione postuma. Additivo nel payload. */
export function lateEntryMarker(
  dayKey: string,
  operationalTime?: string,
): { recorded_late: true; operational_date: string; operational_time?: string } {
  return {
    recorded_late: true,
    operational_date: dayKey,
    ...(operationalTime ? { operational_time: operationalTime } : {}),
  };
}

/** Etichetta leggibile del giorno (es. "mar 24/06") per le intestazioni. */
export function formatDayHeading(dayKey: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dayKey);
  if (!m) return dayKey;
  const [, y, mo, d] = m;
  const date = new Date(Number(y), Number(mo) - 1, Number(d));
  const wd = date.toLocaleDateString('it-IT', { weekday: 'short' });
  return `${wd} ${d}/${mo}/${y.slice(2)}`;
}

export interface MissingDay<T> {
  /** 'YYYY-MM-DD' locale. */
  date: string;
  /** Voci mancanti per quel giorno. */
  items: T[];
}

/** Elenco di chiavi-giorno locali da IERI indietro per `lookbackDays` giorni. */
export function pastDayKeys(todayMs: number, lookbackDays: number): string[] {
  const keys: string[] = [];
  const base = new Date(todayMs);
  for (let i = 1; i <= lookbackDays; i++) {
    const d = new Date(base.getFullYear(), base.getMonth(), base.getDate() - i);
    keys.push(localDateKey(d));
  }
  return keys; // ordine: ieri → più vecchio
}

export interface MissingDailyOpts {
  todayMs: number;
  lookbackDays?: number;
  /** id → ms di creazione: non si mostrano giorni precedenti alla creazione. */
  createdAtMsById?: Map<string, number>;
  /**
   * Se la sorgente "fatto" è stata troncata (cap di fetch raggiunto), non
   * possiamo sapere cosa è stato fatto prima del dato più vecchio disponibile:
   * passare quel ms qui per NON dichiarare "mancante" un giorno non conoscibile.
   */
  earliestTrustedMs?: number | null;
}

/**
 * Voci a frequenza GIORNALIERA mancanti per ciascun giorno passato.
 * Per ogni giorno (ieri → indietro), una voce è "mancante" se:
 *  - esisteva già quel giorno (created_at ≤ fine giornata), e
 *  - non risulta eseguita quel giorno (day key non in doneDayKeysById[id]), e
 *  - il giorno non è anteriore al dato "fatto" più vecchio conosciuto (anti-troncamento).
 * Ritorna solo i giorni con almeno una voce mancante, ordinati ieri → indietro.
 */
export function missingDailyDays<T extends { id: string }>(
  items: T[],
  doneDayKeysById: Map<string, Set<string>>,
  opts: MissingDailyOpts,
): MissingDay<T>[] {
  const lookback = opts.lookbackDays ?? LATE_ENTRY_LOOKBACK_DAYS;
  const out: MissingDay<T>[] = [];
  for (const dayKey of pastDayKeys(opts.todayMs, lookback)) {
    const dayStartMs = startOfDayMs(dayKey);
    const dayEndMs = dayStartMs + 24 * 3_600_000 - 1;
    if (opts.earliestTrustedMs != null && dayEndMs < opts.earliestTrustedMs) continue;
    const missing: T[] = [];
    for (const it of items) {
      const created = opts.createdAtMsById?.get(it.id);
      if (created != null && created > dayEndMs) continue; // non esisteva ancora
      const done = doneDayKeysById.get(it.id);
      if (done && done.has(dayKey)) continue; // già fatto quel giorno
      missing.push(it);
    }
    if (missing.length > 0) out.push({ date: dayKey, items: missing });
  }
  return out;
}

/** ms di mezzanotte LOCALE di una chiave-giorno. */
export function startOfDayMs(dayKey: string): number {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dayKey);
  if (!m) return NaN;
  const [, y, mo, d] = m;
  return new Date(Number(y), Number(mo) - 1, Number(d), 0, 0, 0, 0).getTime();
}

/**
 * Per una voce RICORRENTE non-giornaliera (settimanale/mensile/…) attualmente
 * scaduta, ritorna la chiave-giorno locale in cui è DIVENTATA scaduta
 * (= (ultimaEsecuzione ?? creazione) + frequenza). Null se non scaduta o se non
 * calcolabile (mai eseguita e senza data di creazione).
 */
export function dueDayKeyIfOverdue(opts: {
  lastDoneMs?: number;
  createdAtMs?: number;
  freqHours: number;
  nowMs: number;
}): string | null {
  if (!Number.isFinite(opts.freqHours)) return null;
  const anchor = opts.lastDoneMs ?? opts.createdAtMs;
  if (anchor == null) return null;
  const dueMs = anchor + opts.freqHours * 3_600_000;
  if (opts.nowMs <= dueMs) return null; // non ancora scaduta
  return localDateKey(new Date(dueMs));
}

/**
 * Inserisce voci (di qualunque tipo) in una mappa giorno→voci, fondendo più
 * sorgenti (es. mancanti giornaliere + ricorrenti scadute attribuite al loro
 * giorno di scadenza) e restituendo i giorni ordinati dal più recente.
 */
export function mergeMissingDays<T>(...sources: MissingDay<T>[][]): MissingDay<T>[] {
  const byDay = new Map<string, T[]>();
  for (const src of sources) {
    for (const { date, items } of src) {
      const cur = byDay.get(date) ?? [];
      cur.push(...items);
      byDay.set(date, cur);
    }
  }
  return [...byDay.entries()]
    .map(([date, items]) => ({ date, items }))
    .sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));
}
